// Store centralizado para los colores de los cursos
// Garantiza consistencia de colores en toda la aplicación

import { COURSE_COLORS } from '@/constants/colors'

class CourseColorsStore {
  constructor() {
    this.colorMap = new Map() // Mapa de courseId -> color
    this.usedColors = new Set() // Colores ya utilizados
    this.colorIndex = 0 // Índice para asignar colores secuencialmente
  }

  // Generar un hash único basado en un string
  generateHash(str) {
    let hash = 0
    const string = str.toString()
    
    for (let i = 0; i < string.length; i++) {
      const char = string.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convertir a entero de 32 bits
    }
    
    return hash
  }

  // Obtener o asignar un color para un curso
  getCourseColor(courseId, subjectName = '') {
    // Crear una clave única para el curso
    const uniqueKey = `${courseId}-${subjectName}`
    
    // Si ya tenemos un color asignado para este curso, devolverlo
    if (this.colorMap.has(uniqueKey)) {
      return this.colorMap.get(uniqueKey)
    }
    
    // Asignar el siguiente color disponible de forma secuencial
    // Esto garantiza que no se repitan colores
    let assignedColor = null
    
    // Buscar el primer color disponible que no esté en uso
    for (let i = 0; i < COURSE_COLORS.length; i++) {
      const color = COURSE_COLORS[i]
      if (!this.usedColors.has(color)) {
        assignedColor = color
        break
      }
    }
    
    // Si no hay colores disponibles, usar el color por defecto
    if (!assignedColor) {
      assignedColor = '#3691D8' // Color por defecto
    }
    
    // Guardar el color asignado
    this.colorMap.set(uniqueKey, assignedColor)
    this.usedColors.add(assignedColor)
    return assignedColor
  }

  // Obtener todos los colores asignados (para debug)
  getAllAssignedColors() {
    return Object.fromEntries(this.colorMap)
  }

  // Limpiar el store (útil para testing)
  clear() {
    this.colorMap.clear()
    this.usedColors.clear()
    this.colorIndex = 0
  }

  // Verificar si un color ya está en uso
  isColorUsed(color) {
    return this.usedColors.has(color)
  }

  // Liberar un color cuando se deselecciona un curso
  releaseColor(courseId, subjectName = '') {
    const uniqueKey = `${courseId}-${subjectName}`
    if (this.colorMap.has(uniqueKey)) {
      const color = this.colorMap.get(uniqueKey)
      this.colorMap.delete(uniqueKey)
      this.usedColors.delete(color)
    }
  }

  // Liberar todos los colores (útil para resetear)
  releaseAllColors() {
    this.colorMap.clear()
    this.usedColors.clear()
    this.colorIndex = 0
  }

  // Forzar reasignación de colores (útil cuando se cambia la paleta)
  forceReassignColors() {
    const currentAssignments = Object.fromEntries(this.colorMap)
    this.releaseAllColors()
    
    // Reasignar colores con la nueva paleta
    Object.entries(currentAssignments).forEach(([key]) => {
      const [courseId, subjectName] = key.split('-')
      this.getCourseColor(courseId, subjectName)
    })
  }

  // Obtener estadísticas del store
  getStats() {
    return {
      totalAssigned: this.colorMap.size,
      colorsUsed: this.usedColors.size,
      availableColors: COURSE_COLORS.length - this.usedColors.size
    }
  }
}

// Crear una instancia singleton del store
const courseColorsStore = new CourseColorsStore()

export default courseColorsStore
